// System → Groups — Owner-only (docs/design/console-personas.md §3.4).
// Merged invariants: exactly-one-default; create/edit/set-default; NO delete.

import type { AdminClient } from "../api.js";
import { clear, h } from "../dom.js";
import type { Company, CompanySlug, DashboardGroup, StaffRole } from "../types.js";
import { disclosure } from "../ui/components.js";
import * as face from "../ui/face.js";
import { readerRole } from "../ui/facts.js";
import { failureCopy } from "../ui/failure.js";
import { createFreshnessCaption } from "../ui/freshness.js";
import { navLabel } from "../ui/sentences.js";
import { runWrite } from "../ui/write.js";

export interface GroupsPageOptions {
  client: AdminClient;
  root: HTMLElement;
  isCurrent?: () => boolean;
  /** Who is reading; the shell passes it where it knows (optional). */
  role?: StaffRole;
  /** Open create form immediately (switcher "New group…"). */
  startCreate?: boolean;
  /**
   * Called after a successful create, edit or make-default so the shell can
   * refresh its own group switcher (rd round 11, R8-06). Optional: a caller
   * that does not need it passes nothing, and R8c wires the shell's one.
   */
  onChanged?: () => void | Promise<void>;
}

export async function renderGroupsPage(opts: GroupsPageOptions): Promise<void> {
  const { client, root } = opts;
  const role = readerRole(opts.role);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);

  root.append(h("h1", { class: "page-title" }, [navLabel("groups")]));
  root.append(
    h("p", { class: "lede" }, [
      "Save a set of companies so Home can show them together. One set is the default.",
    ]),
  );

  const freshnessHost = h("div", { "data-role": "freshness-host" });
  root.append(freshnessHost);

  const banner = h("div", {
    class: "banner banner-err",
    role: "alert",
    "data-role": "groups-error",
    hidden: "true",
  });
  root.append(banner);

  const content = h("div", { "data-role": "groups-content" });
  root.append(content);
  content.append(h("p", { class: "muted" }, ["Loading…"]));

  let companies: Company[] = [];
  let groups: DashboardGroup[] = [];
  let directory: face.CompanyDirectory = face.createCompanyDirectory([], { complete: false });
  let loadedAt = new Date();
  let openCreateOnce = opts.startCreate === true;
  /**
   * Every read and every write takes a generation. A read that lands after a
   * later write or read is stale and is dropped, so a held Refresh cannot paint
   * over a save that already succeeded (rd round 11 review, MEDIUM 3).
   */
  let generation = 0;
  /**
   * Saves in flight. A counter, not a flag: with two saves overlapping the
   * first one finishing must not re-enable Refresh while the second is still
   * out (rd round 11 review, SHOULD-FIX 5).
   */
  let savePending = 0;

  /** A save is between the click and its reload: Refresh must not race it. */
  const setRefreshDisabled = (disabled: boolean): void => {
    const refresh = freshnessHost.querySelector<HTMLButtonElement>('[data-action="refresh"]');
    if (refresh !== null) refresh.disabled = disabled;
  };

  const paintFreshness = (): void => {
    clear(freshnessHost);
    freshnessHost.append(
      createFreshnessCaption({
        updatedAt: loadedAt,
        onRefresh: () => {
          void reload();
        },
      }),
    );
    setRefreshDisabled(savePending > 0);
  };

  const hideBanner = (): void => {
    clear(banner);
    banner.hidden = true;
    banner.setAttribute("hidden", "true");
  };

  /**
   * Tell the shell a group changed (R8-06). Never inside the save's own
   * failure face: the save already happened, so a refresh problem here must not
   * be reported as "did not save".
   */
  const notifyChanged = async (): Promise<void> => {
    try {
      await opts.onChanged?.();
    } catch {
      // The page's own state is already true; the switcher catches up on the
      // next render.
    }
  };

  const showError = (message: string, detail?: string): void => {
    clear(banner);
    banner.hidden = false;
    banner.removeAttribute("hidden");
    banner.append(h("p", {}, [message]));
    if (detail !== undefined && detail !== "") {
      banner.append(disclosure("Show details", h("p", {}, [detail])));
    }
  };

  const reload = async (): Promise<void> => {
    const mine = ++generation;
    hideBanner();
    clear(content);
    content.append(h("p", { class: "muted" }, ["Loading…"]));
    try {
      const [companiesRes, groupsRes] = await Promise.all([
        client.listCompanies(),
        client.listDashboardGroups(),
      ]);
      if (!stillCurrent() || mine !== generation) return;
      companies = companiesRes.companies.filter((c) => c.isActive);
      groups = groupsRes.groups;
      directory = face.createCompanyDirectory(
        companies.map((row) => ({ slug: row.slug, displayName: row.displayName })),
        { complete: true },
      );
      loadedAt = new Date();
      paintFreshness();
      const shouldOpen = openCreateOnce;
      openCreateOnce = false;
      paintList(shouldOpen);
    } catch (error) {
      if (!stillCurrent() || mine !== generation) return;
      clear(content);
      const copy = failureCopy(error, { role, context: "read" });
      showError("We could not load groups. Try again.", copy.detail ?? undefined);
      const retry = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "retry",
          "aria-label": "Retry loading groups",
        },
        ["Retry"],
      );
      retry.addEventListener("click", () => {
        void reload();
      });
      content.append(retry);
    }
  };

  const paintList = (openCreate: boolean): void => {
    clear(content);
    const toolbar = h("div", { class: "groups-toolbar" });
    const createBtn = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "group-create",
        "aria-label": "New group",
      },
      ["New group…"],
    );
    createBtn.addEventListener("click", () => {
      paintEditor(null);
    });
    toolbar.append(createBtn);
    content.append(toolbar);

    if (groups.length === 0) {
      content.append(
        h("p", { class: "empty", "data-role": "groups-empty" }, [
          "No groups yet. A group is a set of companies you look at together — like your operating companies.",
        ]),
      );
      if (openCreate) paintEditor(null);
      return;
    }

    const list = h("ul", { class: "groups-list", "data-role": "groups-list" });
    for (const group of groups) {
      list.append(renderGroupCard(group));
    }
    content.append(list);
    if (openCreate) paintEditor(null);
  };

  const renderGroupCard = (group: DashboardGroup): HTMLLIElement => {
    const chips = h("div", { class: "group-chips" });
    for (const slug of group.memberSlugs) {
      chips.append(
        h("span", { class: "group-chip" }, [face.company(slug, directory, "sentence")]),
      );
    }

    const actions = h("div", { class: "group-actions" });
    const edit = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "group-edit",
        "data-group-id": group.id,
        "aria-label": `Edit ${group.name}`,
      },
      ["Edit"],
    );
    edit.addEventListener("click", () => {
      paintEditor(group);
    });
    actions.append(edit);

    if (!group.isDefault) {
      const makeDefault = h(
        "button",
        {
          type: "button",
          class: "btn btn-secondary",
          "data-action": "group-set-default",
          "data-group-id": group.id,
          "aria-label": `Make ${group.name} the default`,
        },
        ["Make default"],
      );
      makeDefault.addEventListener("click", () => {
        // One press, one request (rd round 10): two clicks must not send two
        // default changes. `runWrite` is the one place that guarantees it.
        void (async () => {
          // Any read already in flight is older than this decision.
          generation += 1;
          const outcome = await runWrite({
            key: `group-default:${group.id}`,
            scope: actions,
            gate: () => null,
            send: () => client.setDefaultDashboardGroup(group.id),
            stillCurrent,
          });
          if (outcome.kind === "landed") {
            // The change landed; the shell hears about it even if this page
            // has been navigated away from (rd round 11 review, SHOULD-FIX 6).
            // `runWrite` gates `afterLanded` on stillCurrent, so the shell
            // notification stays here, outside it.
            void notifyChanged();
            if (!stillCurrent()) return;
            await reload();
            return;
          }
          if (!stillCurrent()) return;
          const copy = failureCopy(outcome.error, { role, context: "write" });
          showError(copy.sentence, copy.detail ?? undefined);
        })();
      });
      actions.append(makeDefault);
    }

    return h("li", {
      class: "group-card",
      "data-role": "group-card",
      "data-group-id": group.id,
    }, [
      h("div", { class: "group-card-head" }, [
        h("h2", { class: "group-name" }, [group.name]),
        group.isDefault
          ? h("span", { class: "group-default-badge", "data-role": "default-badge" }, [
              "Default",
            ])
          : null,
      ]),
      chips,
      actions,
    ]);
  };

  const paintEditor = (existing: DashboardGroup | null): void => {
    const prior = content.querySelector('[data-role="group-editor"]');
    prior?.remove();

    const selected = new Set(existing?.memberSlugs ?? []);
    const nameInput = h("input", {
      type: "text",
      class: "input",
      name: "group-name",
      "aria-label": "Group name",
      "data-role": "group-name",
      value: existing?.name ?? "",
      placeholder: "e.g. Operating Cos",
      autocomplete: "off",
    });

    const checklist = h("div", {
      class: "group-checklist",
      "data-role": "group-checklist",
    });
    for (const [index, company] of companies.entries()) {
      // The id is local to this form; never the slug a person could read.
      const id = `group-member-${String(index)}`;
      const checkbox = h("input", {
        type: "checkbox",
        id,
        name: "member",
        value: company.slug,
        "data-slug": company.slug,
      });
      if (selected.has(company.slug)) checkbox.checked = true;
      checklist.append(
        h("label", { class: "group-check", for: id }, [
          checkbox,
          face.company(company.slug, directory, "sentence"),
        ]),
      );
    }

    const inlineError = h("p", {
      class: "inline-error",
      "data-role": "group-editor-error",
      hidden: "true",
    });

    /** The shared sentence on the face; the server's own words in Show details. */
    const showInlineError = (message: string, detail?: string): void => {
      clear(inlineError);
      inlineError.hidden = false;
      inlineError.removeAttribute("hidden");
      inlineError.append(document.createTextNode(message));
      if (detail !== undefined) {
        inlineError.append(disclosure("Show details", h("p", {}, [detail])));
      }
    };

    /** Announce the gate's own reason and hand it back to `runWrite`. */
    const noteInlineRefusal = (reason: string): string => {
      showInlineError(reason);
      return reason;
    };

    /** The ticked companies, in the order the editor shows them. */
    const selectedMembers = (): CompanySlug[] =>
      [...checklist.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')]
        .filter((el) => el.checked)
        .map((el) => el.value);

    const save = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "group-save",
        "aria-label": existing ? "Save group" : "Create group",
      },
      [existing ? "Save" : "Create"],
    );
    const cancel = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "group-cancel",
        "aria-label": "Cancel",
      },
      ["Cancel"],
    );

    const editor = h("div", {
      class: "group-editor",
      "data-role": "group-editor",
    }, [
      h("h2", { class: "group-editor-title" }, [
        existing ? "Edit group" : "New group",
      ]),
      h("label", { class: "field-label" }, ["Name", nameInput]),
      h("p", { class: "field-label" }, ["Companies"]),
      checklist,
      inlineError,
      h("div", { class: "group-editor-actions" }, [save, cancel]),
    ]);

    cancel.addEventListener("click", () => {
      editor.remove();
    });

    save.addEventListener("click", () => {
      // One press, one group (rd round 10): `runWrite` disables the whole
      // editor scope, so a second click, a held key or a programmatic press
      // shares the first run (item B3b-4).
      void (async () => {
        clear(inlineError);
        inlineError.hidden = true;
        inlineError.setAttribute("hidden", "true");
        // Refresh must not race this save: a held read that lands later is
        // stale, and a failure must stay on the page banner (MEDIUM 3).
        generation += 1;
        savePending += 1;
        setRefreshDisabled(true);
        const outcome = await runWrite({
          key: `group-save:${existing?.id ?? "new"}`,
          scope: editor,
          gate: () => {
            // The one gate every submit path calls. A refusal sends nothing,
            // so the outcome is `refused(status 0)` and the gate has already
            // said why on the inline line.
            const name = nameInput.value.trim();
            if (name.length === 0) return noteInlineRefusal("Name is required.");
            const picked = selectedMembers();
            if (picked.length === 0) return noteInlineRefusal("Pick at least one company.");
            return null;
          },
          send: () =>
            existing
              ? client.updateDashboardGroup(existing.id, {
                  name: nameInput.value.trim(),
                  memberSlugs: selectedMembers(),
                })
              : client.createDashboardGroup({
                  name: nameInput.value.trim(),
                  memberSlugs: selectedMembers(),
                }),
          stillCurrent,
        });
        savePending = Math.max(0, savePending - 1);
        if (stillCurrent()) setRefreshDisabled(savePending > 0);
        if (outcome.kind === "landed") {
          // A save that finishes after navigating away still refreshes the
          // switcher (rd round 11, SHOULD-FIX 6). It is not a re-read, so it
          // sits outside `afterLanded` (which `stillCurrent` gates).
          void notifyChanged();
          if (!stillCurrent()) return;
          await reload();
          return;
        }
        // A gate refusal already announced its own sentence in `live`.
        if (outcome.kind === "refused" && outcome.status === 0) return;
        if (!stillCurrent()) return;
        const copy = failureCopy(outcome.error, { role, context: "write" });
        showInlineError(copy.sentence, copy.detail ?? undefined);
        showError(copy.sentence, copy.detail ?? undefined);
      })();
    });

    content.prepend(editor);
    nameInput.focus();
  };

  paintFreshness();
  await reload();
}
